import React, { useState } from "react";
import { NavLink, Link, useParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import logo from "../../assets/logo/Westlake -Village-Hospice-Logo.svg";
import { SUPPORTED_LANGS, DEFAULT_LANG } from "../../utils/langUtils";

const languageFlags = {
  en: "US",
  ar: "AR",
  es: "ES",
  fa: "IR",
  hy: "AM",
  ru: "RU",
  tl: "PH",
  "zh-CN": "CN",
  ko: "KR",
  "hi-Guj": "IN",
};

// UI languages list
const languages = [
  { code: "en", name: "English" },
  { code: "ar", name: "العربية" },
  { code: "es", name: "Español" },
  { code: "fa", name: "فارسی" },
  { code: "ru", name: "Русский" },
  { code: "tl", name: "Tagalog" },
  { code: "zh-CN", name: "中文(简体)" },
  { code: "ko", name: "한국어" },
  { code: "hy", name: "Հայերեն" },
  { code: "hi-Guj", name: "हिन्दी–ગુજરાતી" },
];

export default function Header() {
  const { t, i18n } = useTranslation();
  const { lang: urlLang } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuActive, setIsMenuActive] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);

  // If we are on a valid lang route, urlLang is populated.
  // If we are on /admin or root, it might be undefined.
  // We use current valid urlLang or fall back to default for constructing links.
  const currentRouteLang = SUPPORTED_LANGS.includes(urlLang) ? urlLang : DEFAULT_LANG;

  // Visual flag: use i18n language, or urlLang if available
  const displayLang = SUPPORTED_LANGS.includes(urlLang) ? urlLang : (i18n.language || DEFAULT_LANG);

  const changeLanguage = (targetCode) => {
    // 1. Construct new path with swapped language
    // We rely on current pathname.
    // If we are on /en/about -> /zh-CN/about
    // If we are on /admin/login -> /admin/login (actually we shouldn't allow lang switch on admin to route out of admin?)
    // BUT requirement says: "When I navigate from page to page, it must keep the same language prefix".
    // Admin routes are non-prefixed.
    // If user switches lang on admin page, maybe we just change i18n? 
    // The requirement says "admin routes ... unchanged". 
    // Assuming headers might be hidden on admin or different layout. AdminLayout uses its own sidebar usually.
    // But if Header IS used on Admin, we should just change i18n without routing?
    // PublicShell is the one rendering Header. AdminLayout does NOT use PublicShell?
    // Step 382 App.jsx shows AdminLayout is separate.
    // So Header is ONLY present on PublicShell routes (/:lang/*).
    // So location.pathname ALWAYS starts with /:lang.

    // Helper function to swap the language segment in a path
    const swapLang = (pathname, newLang) => {
      const segments = pathname.split('/').filter(Boolean);
      if (segments.length > 0 && SUPPORTED_LANGS.includes(segments[0])) {
        segments[0] = newLang;
      } else {
        // This case should ideally not happen if Header is only on /:lang/* routes
        segments.unshift(newLang);
      }
      return `/${segments.join('/')}`;
    };

    const newPath = swapLang(location.pathname, targetCode);
    navigate(`${newPath}${location.search}${location.hash}`);

    // i18n sync happens in LanguageLayout automatically upon navigation.
    setIsLangOpen(false);
    setIsMenuActive(false);
  };

  const navItems = [
    { to: `/${currentRouteLang}`, label: "Home" },
    { to: `/${currentRouteLang}/medicare-hospice`, label: "Hospice" },
    { to: `/${currentRouteLang}/services`, label: "Services" },
    { to: `/${currentRouteLang}/locations`, label: "Service Areas" },
    { to: `/${currentRouteLang}/insurances`, label: "Insurances" },
    { to: `/${currentRouteLang}/about`, label: "About" },
    { to: `/${currentRouteLang}/referrals`, label: "Referrals" },
    { to: `/${currentRouteLang}/contact`, label: "Contact Us" },
  ];

  return (
    <header className="relative z-50 bg-white shadow-lg py-2">
      <div className="mx-[30px] px-[15px] flex items-center justify-between">
        {/* Logo */}
        <Link to={`/${currentRouteLang}`}>
          <img
            className="w-[250px] h-auto object-contain block"
            src={logo}
            alt="Logo"
          />
        </Link>

        {/* Desktop Navigation + Language */}
        <div className="hidden md:flex items-center gap-10">
          <nav>
            <ul className="flex gap-8 text-[#33528b] text-[16px] font-bold">
              {navItems.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.to === `/${currentRouteLang}`}
                    className={({ isActive }) =>
                      `hover:text-blue-600 transition ${isActive ? "text-[#33528b]" : "text-[#33528b]"}`
                    }
                  >
                    {t(item.label)}
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>

          {/* Language Switcher - Desktop */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setIsLangOpen(!isLangOpen)}
              aria-label={
                isLangOpen ? "Close language menu" : "Open language menu"
              }
              className="flex items-center gap-2 text-[#33528b] hover:text-blue-600 transition font-medium"
            >
              <span>{languageFlags[displayLang] || "US"}</span>
              <span className="hidden lg:inline">
                {t(languages.find((l) => l.code === displayLang)?.name || "English")}
              </span>
              <svg
                className={`w-4 h-4 transition-transform ${isLangOpen ? "rotate-180" : ""}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>

            {isLangOpen && (
              <div className="absolute top-full mt-3 right-0 bg-white rounded-lg shadow-2xl py-3 min-w-[200px] z-50">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => !lang.soon && changeLanguage(lang.code)}
                    disabled={lang.soon}
                    className={`w-full px-5 py-3 text-right flex items-center justify-between gap-3 transition ${lang.soon ? "opacity-50 cursor-not-allowed grayscale" : "hover:bg-blue-50"} ${displayLang === lang.code
                      ? "bg-blue-100 text-blue-700 font-bold"
                      : "text-gray-800"
                      }`}
                  >
                    <div className="flex items-center gap-3">
                      <span>{languageFlags[lang.code]}</span>
                      <span>{t(lang.name)}</span>
                    </div>
                    {lang.soon && (
                      <span className="text-[10px] bg-amber-100 text-amber-600 px-2 py-0.5 rounded-full font-bold uppercase tracking-wider">
                        {t("soon")}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Mobile Menu Button */}
        <button
          type="button"
          aria-label={isMenuActive ? "Close menu" : "Open menu"}
          className={`md:hidden space-y-1 ${isMenuActive ? "active" : ""}`}
          onClick={() => setIsMenuActive(!isMenuActive)}
        >
          <span
            className="block w-8 h-0.5 bg-[#33528b]"
            aria-hidden="true"
          ></span>
          <span
            className="block w-8 h-0.5 bg-[#33528b]"
            aria-hidden="true"
          ></span>
          <span
            className="block w-8 h-0.5 bg-[#33528b]"
            aria-hidden="true"
          ></span>
        </button>
      </div>

      {/* Mobile Menu */}
      {isMenuActive && (
        <div className="md:hidden bg-white border-t">
          <div className="p-6 space-y-6">
            <ul className="space-y-5 text-xl font-bold text-gray-800">
              {navItems.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.to === `/${currentRouteLang}`}
                    onClick={() => setIsMenuActive(false)}
                    className="block hover:text-blue-600 transition"
                  >
                    {t(item.label)}
                  </NavLink>
                </li>
              ))}
            </ul>

            <div className="border-t pt-6">
              <p className="text-gray-600 font-semibold mb-4">
                {t("Language")}
              </p>
              <div className="grid grid-cols-2 gap-3">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => !lang.soon && changeLanguage(lang.code)}
                    disabled={lang.soon}
                    className={`py-4 rounded-xl border-2 font-medium transition relative overflow-hidden ${lang.soon
                      ? "opacity-60 grayscale cursor-not-allowed border-gray-200 bg-gray-100"
                      : displayLang === lang.code
                        ? "bg-blue-600 text-white border-blue-600"
                        : "bg-gray-50 border-gray-300 text-gray-800"
                      } flex flex-col items-center justify-center gap-1`}
                  >
                    <div className="flex items-center gap-2">
                      <span>{languageFlags[lang.code]}</span>
                      <span className="text-sm">{t(lang.name)}</span>
                    </div>
                    {lang.soon && (
                      <span className="text-[9px] bg-amber-500 text-white px-2 py-0.5 rounded-full font-black uppercase leading-none">
                        {t("soon")}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

